import { isDefined, isEmpty } from '@sgme/fp';
import { intersection } from 'lodash-es';
import type { ProductVariation, RequestType, SelectedProductVariation } from 'models/productsReferential';
import { createSelector } from 'reselect';
import { getFormEnrichedProductSubType, getFormEnrichedProductType } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { getTabWorkspaceType } from 'state/workspaces/workspaces/workspaces.selector';

import type { ProductsReferentialState } from './productsReferential.model';

function selectProductsReferentialState(state: AppState): ProductsReferentialState {
  return state.productsReferential;
}

const noProductVariations: readonly [] = [];

export const selectEssentialProductVariations = (state: AppState) =>
  state?.productsReferential?.essential ?? noProductVariations;

export function extractProductVariations(state: ProductsReferentialState, view: WorkspaceType) {
  switch (view) {
    case 'static':
    case 'dynamic':
      return state?.[view] ?? noProductVariations;
    case 'newBatch':
      return state?.batch ?? noProductVariations;
    case 'essential':
      return state?.essential ?? noProductVariations;
    default:
      return noProductVariations;
  }
}

const getProductVariations = createSelector(
  [
    (appState: AppState, _: WorkspaceType) => selectProductsReferentialState(appState),
    (_: AppState, view: WorkspaceType) => view,
  ],
  (state, view) => extractProductVariations(state, view),
);

export const getAvailableProductVariations: (
  _: AppState,
  __: WorkspaceType,
  filteredByAssetType?: string,
) => readonly ProductVariation[] = createSelector(
  [
    (appState: AppState, view: WorkspaceType, _?: string) => getProductVariations(appState, view),
    (_: AppState, __: WorkspaceType, filteredByAssetType?: string) => filteredByAssetType,
  ],
  (productVariations, filteredByAssetType) =>
    filteredByAssetType === undefined || filteredByAssetType === 'All'
      ? productVariations
      : productVariations.filter(product => product.assetTypes?.includes(filteredByAssetType)),
);

export function getFormProductVariation(
  appState: AppState,
  workspace: WorkspaceType,
  formId: string,
): ProductVariation | undefined {
  const state = selectProductsReferentialState(appState);
  const productVariations = extractProductVariations(state, workspace);
  const productSubType = getFormEnrichedProductSubType(appState, formId);
  const productType = getFormEnrichedProductType(appState, formId);
  return productVariations.find(
    variation => variation.productSubType === productSubType && variation.productType === productType,
  );
}

export function getFormRequestType(
  appState: AppState,
  workspace: WorkspaceType | undefined,
  formId: string,
): RequestType | undefined {
  const foundWorkspace = workspace ?? getTabWorkspaceType(appState, formId);
  return isDefined(foundWorkspace) ? getFormProductVariation(appState, foundWorkspace, formId)?.requestType : undefined;
}

export function hasNoProductAvailable(state: AppState, view: WorkspaceType): boolean {
  return isEmpty(getAvailableProductVariations(state, view));
}

export function getProductReferentialLoadingStatus(state: AppState, view: WorkspaceType) {
  const status = state.productsReferential.status;
  if (status !== 'loaded') {
    return status;
  }

  if (hasNoProductAvailable(state, view)) {
    return 'empty';
  }
  return 'loaded';
}

export function getProductTypes(state: AppState, view: WorkspaceType) {
  const variations = getAvailableProductVariations(state, view);
  const productTypes = variations.map(({ productType }) => productType);

  return [...new Set(productTypes)].sort();
}

export function getProductSubTypes(state: AppState, view: WorkspaceType, productType: string) {
  const variations = getAvailableProductVariations(state, view);
  const productSubTypes = variations.filter(v => v.productType === productType).map(v => v.productSubType);
  return [...new Set(productSubTypes)].sort();
}

export function getProductAssetTypesByWorkspace(state: AppState, view: WorkspaceType, productSubType: string) {
  const variations = getAvailableProductVariations(state, view);
  return variations.find(p => p.productSubType === productSubType)?.assetTypes;
}

export function getWrappers(state: AppState, view: WorkspaceType, productType: string, productSubType: string) {
  const variations = getAvailableProductVariations(state, view);
  const wrappers = variations
    .filter(variation => variation.productType === productType && variation.productSubType === productSubType)
    .map(variation => variation.wrapper);
  return [...new Set(wrappers)].sort();
}

export const getAvailablePayoffTypes = createSelector(
  (appState: AppState, workspace: WorkspaceWithProductSelector) => getAvailableProductVariations(appState, workspace),
  (
    _appState: AppState,
    _workspace: WorkspaceWithProductSelector,
    selectedProductVariation: Partial<SelectedProductVariation>,
  ) => selectedProductVariation,
  (availableProductVariations, { productType, productSubType, wrapper }): readonly string[] => {
    if (!availableProductVariations) {
      return [];
    }

    const listOfPayoffTypes = availableProductVariations
      .filter(
        v =>
          (productType ? v.productType === productType : true) &&
          (productSubType ? v.productSubType === productSubType : true) &&
          (wrapper ? v.wrapper === wrapper : true),
      )
      .map(v => v.payoffTypes ?? []);

    return intersection(...listOfPayoffTypes);
  },
);

export function getAssetTypeFilters(appState: AppState): string[] | undefined {
  const state = selectProductsReferentialState(appState);
  return state.availableAssetTypes;
}

export const selectProductsReferentialStatus = createSelector(
  [(appState: AppState) => appState.productsReferential.status],
  status => {
    const isEmpty = !status || status === 'empty';
    const isSuccess = status === 'loaded';
    const isError = status === 'loading_failed';
    const isLoading = status === 'not_yet_loaded';

    return { isError, isLoading, isSuccess, status, isEmpty };
  },
);
